import React, { useCallback, useState } from 'react'

import TripPlaceFieldDesktop from './TripDetailsFields/TripPlaceFieldDesktop'

import { getPlaceById } from 'api/search'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import MessageBanner from 'components/Luxkit/Banners/MessageBanner'
import InputChip from 'components/Luxkit/Chips/InputChip'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import ModalFooter from 'components/Luxkit/Modal/ModalFooter'
import { ModalHeaderWrapper } from 'components/Luxkit/Modal/ModalHeaderWrapper'
import Group from 'components/utils/Group'
import useStateWithRef from 'hooks/useStateWithRef'
import { EmptyArray } from 'lib/arrayUtils'
import { useUpdateTrip } from 'tripPlanner/hooks/api'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'

interface PlaceSelection {
  id: string
  name: string
}

interface Props {
  tripId: string
  initialDestinations?: Array<PlaceSelection>
}

const DesktopUpdateTripDestinations = ({
  tripId,
  initialDestinations = EmptyArray,
}: Props) => {
  const [selectedPlaces, setSelectedPlaces, selectedPlacesRef] =
    useStateWithRef<Array<PlaceSelection>>(initialDestinations)
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined)

  const [inputValue, setInputValue] = useState('')
  const hasInput = !!inputValue

  const { closeModal, goBack } = useModalCallbacks()

  const { mutate, isLoading: isSaving } = useUpdateTrip({
    onSuccess: () => {
      closeModal()
    },
    onError: () => {
      setErrorMsg('Unable to update trip destinations')
    },
  })

  const onClearSelectedPlace = (placeId: string) => {
    setSelectedPlaces((prev) => prev.filter((p) => p.id !== placeId))
  }

  const onLocationSelect = useCallback(
    (selection: PlaceSelection) => {
      const placeId = selection.id
      if (!selectedPlaces.find((p) => p.id === placeId)) {
        setSelectedPlaces((prev) => {
          return [
            ...prev,
            {
              id: placeId,
              name: selection.name,
            },
          ]
        })
        // Typeahead API doesn't include country code, so do a separate request to get that
        getPlaceById(placeId)
          .then((place) => {
            if (place.type !== 'country' && place.countryCode) {
              const name = `${place.name}, ${place.countryCode}`
              setSelectedPlaces((prev) =>
                prev.map((p) => (p.id === placeId ? { ...p, name } : p)),
              )
            }
          })
          .catch(() => {
            // do nothing
          })
      }
      setInputValue('')
    },
    [selectedPlaces, setSelectedPlaces],
  )

  const onSave = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()
      if (!hasInput) {
        const destinationPlaceIds = selectedPlacesRef.current.map((p) => p.id)
        mutate({
          tripId,
          destinationPlaceIds,
        })
      }
    },
    [mutate, selectedPlacesRef, tripId, hasInput],
  )

  return (
    <form onSubmit={onSave}>
      <ModalHeaderWrapper
        title={
          initialDestinations.length > 0
            ? 'Update destinations'
            : 'Add destinations'
        }
        onCloseButtonClick={closeModal}
        onBackButtonClick={goBack}
      />
      <ModalBody>
        <ModalContent>
          <VerticalSpacer gap={12}>
            <TripPlaceFieldDesktop
              inputValue={inputValue}
              onInputChange={setInputValue}
              onSelect={onLocationSelect}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              isInvalidState={!!inputValue}
            />
            <Group
              direction="horizontal"
              horizontalAlign="start"
              gap={8}
              wrap="wrap"
            >
              {selectedPlaces.map((place) => (
                <InputChip
                  key={place.id}
                  variant="filled"
                  onClick={() => onClearSelectedPlace(place.id)}
                >
                  {place.name}
                </InputChip>
              ))}
            </Group>
            {errorMsg && (
              <MessageBanner kind="critical" description={errorMsg} />
            )}
          </VerticalSpacer>
        </ModalContent>
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          children:
            initialDestinations.length > 0 ? 'Save' : 'Add destinations',
          disabled: isSaving,
          type: 'submit',
        }}
        secondaryActionProps={{
          children: 'Cancel',
          disabled: isSaving,
          onClick: closeModal,
        }}
      />
    </form>
  )
}

export default DesktopUpdateTripDestinations
